import type { GameCoord } from "@/engine/coords.ts";
import type { FactionId } from "./factions.ts";
import type { MarkerIcon } from "./markers.ts";
import { PLACE_NAMES } from "./placeNames.ts";

/**
 * Fixed points of interest.
 *
 * Source: the open-source WARDOGS Calculator map definitions (Apollyon, MIT
 * licence), converted from its hundredths-of-a-unit marker space into game
 * coordinates. Underlying game data belongs to BULKHEAD / Team17.
 *
 * Named locations are the one thing none of the community tools have yet —
 * wardogshub says outright that its maps "have no POI markers" and is
 * crowdsourcing them. This is the seed; the real value comes from adding
 * pilot-facing names for ridges, valleys and landing sites, which is what
 * makes a callout usable at 200 km/h.
 */

export type PoiKind =
  "hq" | "tower" | "weaponsVendor" | "garageVendor" | "spawnBoard" | "landmark";

export interface Poi {
  readonly id: string;
  readonly mapId: string;
  readonly kind: PoiKind;
  readonly label: string;
  readonly at: GameCoord;
  /** Set when the point belongs to one faction. */
  readonly faction?: FactionId;
  /** Short aliases people actually say on comms, for search. */
  readonly aliases?: readonly string[];
}

/**
 * The game's own icon for each kind of fixed point.
 *
 * A faction headquarters uses that faction's emblem, exactly as the in-game
 * map does, which is why this takes the whole POI rather than just its kind.
 */
export function poiIcon(poi: Poi): MarkerIcon {
  switch (poi.kind) {
    case "hq":
      return poi.faction ?? "wardogs";
    case "tower":
      return "tower";
    case "weaponsVendor":
      return "weapons_vendor";
    case "garageVendor":
      return "garage_vendor";
    case "spawnBoard":
      return "spawn_board";
    case "landmark":
      return "wardogs";
  }
}

export const POIS: readonly Poi[] = [
  {
    id: "bak-hq-valkyra",
    mapId: "bakurani",
    kind: "hq",
    label: "Valkyra HQ",
    at: { x: 118.75, y: 70.93 },
    faction: "valkyra",
    aliases: ["val", "east base"],
  },
  {
    id: "bak-hq-manticore",
    mapId: "bakurani",
    kind: "hq",
    label: "Manticore HQ",
    at: { x: 40.09, y: 77.52 },
    faction: "manticore",
    aliases: ["mant", "west base"],
  },
  {
    id: "bak-hq-lonestar",
    mapId: "bakurani",
    kind: "hq",
    label: "Lonestar HQ",
    at: { x: 87.46, y: 32.5 },
    faction: "lonestar",
    aliases: ["lone", "south base"],
  },
  {
    // The upstream "valkyra" marker for Ozeti sits at 118.75, 70.93 — byte for
    // byte the Bakurani position, and ~2 km from Ozeti's own Valkyra vendors.
    // The vendor cluster is self-consistent, so it wins.
    id: "oze-hq-valkyra",
    mapId: "ozeti",
    kind: "hq",
    label: "Valkyra HQ",
    at: { x: 138.03, y: 67.33 },
    faction: "valkyra",
    aliases: ["val", "east base"],
  },
  {
    id: "oze-hq-manticore",
    mapId: "ozeti",
    kind: "hq",
    label: "Manticore HQ",
    at: { x: 68.28, y: 88.03 },
    faction: "manticore",
    aliases: ["mant"],
  },
  {
    id: "oze-hq-lonestar",
    mapId: "ozeti",
    kind: "hq",
    label: "Lonestar HQ",
    at: { x: 83.73, y: 30.69 },
    faction: "lonestar",
    aliases: ["lone", "south base"],
  },
  {
    id: "zes-hq-valkyra",
    mapId: "zestafona",
    kind: "hq",
    label: "Valkyra HQ",
    at: { x: 39.44, y: 124.94 },
    faction: "valkyra",
    aliases: ["val", "north-west base"],
  },
  {
    id: "zes-hq-manticore",
    mapId: "zestafona",
    kind: "hq",
    label: "Manticore HQ",
    at: { x: 104.66, y: 115.08 },
    faction: "manticore",
    aliases: ["mant", "east base"],
  },
  {
    id: "zes-hq-lonestar",
    mapId: "zestafona",
    kind: "hq",
    label: "Lonestar HQ",
    at: { x: 68.01, y: 66.6 },
    faction: "lonestar",
    aliases: ["lone", "south base"],
  },
];

/**
 * Every named point on a map: fixed features plus the community place names.
 *
 * Place names are the whole reason this exists. None of the other tools have
 * them — one of them says outright that its maps "have no POI markers" — and a
 * callout nobody can act on is not a callout. Towers come from the place-name
 * dataset rather than the marker dataset, so a single map never mixes two
 * sources that disagree about where things are.
 */
export function poisForMap(mapId: string): Poi[] {
  const fixed = POIS.filter((poi) => poi.mapId === mapId);

  const named: Poi[] = PLACE_NAMES.filter((place) => place.mapId === mapId).map((place) => ({
    id: place.id,
    mapId: place.mapId,
    kind: place.kind,
    label: place.label,
    at: place.at,
  }));

  return [...fixed, ...named];
}

export function headquartersFor(mapId: string, faction: FactionId): Poi | undefined {
  return POIS.find(
    (poi) => poi.mapId === mapId && poi.kind === "hq" && poi.faction === faction,
  );
}

/**
 * Fuzzy search over labels and aliases.
 *
 * Deliberately forgiving: a pilot types three letters with one hand while
 * flying. Matches are ranked so a prefix beats a substring, and an exact
 * label beats everything.
 */
export function searchPois(query: string, mapId: string): Poi[] {
  const needle = query.trim().toLowerCase();
  if (needle.length === 0) return [];

  const scored: Array<{ poi: Poi; score: number }> = [];

  for (const poi of poisForMap(mapId)) {
    const label = poi.label.toLowerCase();
    let score = -1;

    if (label === needle) score = 100;
    else if (label.startsWith(needle)) score = 80;
    else if (label.includes(needle)) score = 60;

    for (const alias of poi.aliases ?? []) {
      const lower = alias.toLowerCase();
      if (lower === needle) score = Math.max(score, 90);
      else if (lower.startsWith(needle)) score = Math.max(score, 70);
      else if (lower.includes(needle)) score = Math.max(score, 50);
    }

    if (score >= 0) scored.push({ poi, score });
  }

  return scored
    .sort((a, b) => b.score - a.score || a.poi.label.localeCompare(b.poi.label))
    .map((entry) => entry.poi);
}
